import {
  Badge,
  Box,
  Button,
  ButtonSpinner,
  Container,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Task, User } from 'api/types';
import { useUserContext } from 'context/UserContextProvider';
import { createDropdownPlainOptions } from 'helpers/general';
import { dropdown } from 'helpers/makeFormFields';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useRetryTaskAutomationMutation from 'hooks/mutations/useRetryTaskAutomationMutation';
import useStartTaskAutomationMutation from 'hooks/mutations/useStartTaskAutomationMutation';
import useUpdateTaskPriorityMutation from 'hooks/mutations/useUpdateTaskPriority';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { FiUser, FiUserPlus, FiUserX } from 'react-icons/fi';
import { RiArrowDownSLine, RiArrowUpDoubleFill, RiArrowUpSLine, RiCircleLine, RiEqualFill, RiPencilLine } from 'react-icons/ri';
import { SiProbot } from 'react-icons/si';
import * as yup from 'yup';

interface UserAssignForm {
  user: string;
}

const schema = yup
  .object({
    user: yup.string().max(255).required(),
  })
  .required();

const TaskBannerCard = ({
  isRegistrationOfPortalInProgress = false,
  showAutomationInProgress = false,
  assignee,
  currentUserId,
  queryToInvalidate,
  opUsers,
  isLoading,
  assign,
  reassign,
  task,
}: {
  isRegistrationOfPortalInProgress?: boolean;
  assignee: User | undefined;
  currentUserId: number | undefined;
  opUsers: Array<User> | [];
  isLoading: boolean;
  assign(): void;
  reassign(id?: number): void;
  queryToInvalidate: () => Promise<void>;
  task: Task;
  showAutomationInProgress?: boolean;
}) => {
  const { user, isMember } = useUserContext();
  const canRetryAutomation = isMember('automations:retry');
  const retryTaskAutomationMutation = useRetryTaskAutomationMutation();
  const startTaskAutomationMutation = useStartTaskAutomationMutation();
  const toast = useToast(defaultToastOptions);
  const [isPriorityModalOpen, setPriorityModalOpen] = useState(false);
  const { isOpen: isOpenAssignSomeone, onOpen: onOpenAssignSomeone, onClose: onCloseAssignSomeone } = useDisclosure();
  const { isOpen: isOpenAssignMe, onOpen: onOpenAssignMe, onClose: onCloseAssignMe } = useDisclosure();
  const { isOpen: isOpenRetryAutomation, onOpen: onOpenRetryAutomation, onClose: onCloseRetryAutomation } = useDisclosure();
  const { isOpen: isOpenStartAutomation, onOpen: onOpenStartAutomation, onClose: onCloseStartAutomation } = useDisclosure();

  const updateTaskPriorityMutation = useUpdateTaskPriorityMutation({ taskId: task.id });

  const { t } = useTranslation();

  const taskPriority = typeof task?.priority === 'number' ? task?.priority : 100;
  const [selectedPriority, setSelectedPriority] = useState<number>(taskPriority);

  const assigneeName = assignee?.name;
  const isTaskUnassigned = task?.status === 0 && assignee === null;

  const isTaskAutomationInProgress = task?.queued && task?.automate;
  const isTaskAutomationQueued = !task?.queued && task?.automate;

  const isTaskAutomated = isTaskAutomationInProgress || isTaskAutomationQueued;

  const userCanChangePriority = user?.permissions.includes('task:update-priority');

  const unassignedText = (
    <Text>{t('components:assign_card.unassigned') + ' - ' + t('components:assign_card.queue_position') + ': ' + task?.queue_position}</Text>
  );

  const PRIORITY_MAP = [
    { value: 100, icon: <RiArrowUpDoubleFill size={26} color="red" />, text: t('components:assign_card.task_priority.urgent') },
    { value: 50, icon: <RiArrowUpSLine size={26} color="red" />, text: t('components:assign_card.task_priority.high') },
    { value: 0, icon: <RiEqualFill size={24} color="orange" />, text: t('components:assign_card.task_priority.medium') },
    { value: -50, icon: <RiArrowDownSLine size={26} color="blue" />, text: t('components:assign_card.task_priority.low') },
    { value: -100, icon: <RiCircleLine size={18} color="gray" />, text: t('components:assign_card.task_priority.non_important') },
  ];

  const {
    handleSubmit,
    formState: { errors },
    control,
    setError,
  } = useForm<UserAssignForm>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const opUserOptions = createDropdownPlainOptions({
    records: opUsers,
    valueColumn: 'id',
    labelColumn: 'name',
  });

  const { mutate } = useMutation<any, Error, UserAssignForm>({
    mutationFn: async (data) => {
      return reassign(parseInt(data?.user));
    },
  });
  const onSubmit: SubmitHandler<UserAssignForm> = (data) => {
    mutate(data, {
      onSuccess: () => onCloseAssignSomeone(),
      onError: (error: any) => {
        const { errors } = error.response.data;

        (Object.keys(errors) as Array<keyof UserAssignForm>).forEach((key) => {
          setError(key, {
            type: 'server',
            message: errors[key]!.join('. '),
          });
        });
      },
    });
  };

  const currentPriorityObj = PRIORITY_MAP.find((p) => p.value === taskPriority) || PRIORITY_MAP[0];
  const currentPriorityTextAndIcon = {
    text: currentPriorityObj.text,
    icon: currentPriorityObj.icon,
  };

  const PriorityButton = ({
    icon,
    text,
    isCurrentPriority,
    isSelected,
    onClick,
    disabled,
  }: {
    icon: React.ReactNode;
    text: string;
    value: number;
    isCurrentPriority: boolean;
    isSelected: boolean;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <Button
      size="lg"
      width="100%"
      borderRadius={8}
      px={4}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bg={isSelected ? 'blue.100' : 'white'}
      border="2px solid"
      borderColor={isSelected ? 'blue.600' : 'gray.300'}
      color="gray.800"
      pointerEvents={disabled ? 'none' : isCurrentPriority ? 'none' : 'auto'}
      tabIndex={disabled ? -1 : isCurrentPriority ? -1 : 0}
      _hover={
        disabled || isCurrentPriority
          ? {
              borderColor: 'gray.300',
              bg: 'white',
              color: 'gray.800',
            }
          : {
              borderColor: 'blue.600',
              bg: 'blue.100',
              color: 'gray.800',
            }
      }
      _active={
        disabled || isCurrentPriority
          ? {
              borderColor: 'gray.300',
              bg: 'white',
              color: 'gray.800',
            }
          : {
              borderColor: 'blue.700',
              bg: 'blue.200',
              color: 'gray.800',
            }
      }
      transition="all 0.2s"
      onClick={onClick}
    >
      <Flex align="center" flex="1" justify="flex-start">
        {icon}
        <Text ml={3} textAlign="left" fontWeight="medium">
          {text}
        </Text>
      </Flex>
      {isCurrentPriority && (
        <Badge colorScheme="blue" borderRadius={4} px={2} py={1} fontWeight="bold" ml={3}>
          {t('components:assign_card.task_priority.current_priority_label')}
        </Badge>
      )}
    </Button>
  );

  const handleDiscard = () => {
    setSelectedPriority(taskPriority);
    setPriorityModalOpen(false);
  };

  const handleConfirm = () => {
    updateTaskPriorityMutation.mutate(selectedPriority, {
      onSuccess: () => {
        queryToInvalidate();
        setPriorityModalOpen(false);
      },
    });
  };

  return (
    <Flex bgColor="#FFFBEB" width="100%" p={6} gap={2} align="center" flexWrap="wrap">
      <Badge bg="#FFF9C4" color="#5D3A00" borderRadius={8} px={3} py={2} fontWeight="bold" border="2px solid #FBC02D">
        {t(`components:assign_card.task_type.${task?.type}`)}
      </Badge>
      {userCanChangePriority ? (
        <Button
          borderRadius={8}
          width="fit-content"
          leftIcon={currentPriorityTextAndIcon.icon}
          rightIcon={<RiPencilLine size={18} />}
          px={4}
          pointerEvents={isRegistrationOfPortalInProgress ? 'none' : 'auto'}
          tabIndex={isRegistrationOfPortalInProgress ? -1 : 0}
          onClick={isRegistrationOfPortalInProgress ? undefined : () => setPriorityModalOpen(true)}
        >
          {currentPriorityTextAndIcon.text}
        </Button>
      ) : (
        <Tooltip label={t('components:assign_card.task_priority.no_permission_tooltip')} hasArrow>
          <span style={{ display: 'inline-block' }}>
            <Button
              borderRadius={8}
              width="fit-content"
              leftIcon={currentPriorityTextAndIcon.icon}
              px={4}
              pointerEvents="none"
              tabIndex={0}
              onClick={undefined}
              _hover={{}}
              _active={{}}
            >
              {currentPriorityTextAndIcon.text}
            </Button>
          </span>
        </Tooltip>
      )}
      {!isRegistrationOfPortalInProgress &&
        (isTaskAutomated && isTaskUnassigned ? (
          <Button
            borderRadius={8}
            width="fit-content"
            leftIcon={<SiProbot size={18} />}
            px={4}
            pointerEvents="none"
            tabIndex={-1}
            colorScheme="purple"
            size="sm"
          >
            {isTaskAutomationInProgress ? t('components:assign_card.automation.in_progress') : t('components:assign_card.automation.queued')}
          </Button>
        ) : isTaskUnassigned ? (
          <Button borderRadius={8} width="fit-content" leftIcon={<FiUserX size={18} />} px={4} pointerEvents="none" tabIndex={-1}>
            {unassignedText}
          </Button>
        ) : showAutomationInProgress ? (
          <Button borderRadius={8} width="fit-content" leftIcon={<SiProbot size={18} />} px={4} pointerEvents="none" tabIndex={-1}>
            {t('components:assign_card.automation.title')}
          </Button>
        ) : (
          <Button borderRadius={8} width="fit-content" leftIcon={<FiUser size={18} />} px={4} pointerEvents="none" tabIndex={-1}>
            {assigneeName}
          </Button>
        ))}

      <Box flex={1} />
      {!isRegistrationOfPortalInProgress && !isTaskAutomationInProgress && (
        <>
          {task.retryPossible && (
            <Button
              borderRadius={8}
              width="fit-content"
              colorScheme="orange"
              px={4}
              leftIcon={<SiProbot size={18} />}
              disabled={retryTaskAutomationMutation.isPending || !canRetryAutomation}
              onClick={onOpenRetryAutomation}
            >
              {t('components:retry_automation_modal.action')}
            </Button>
          )}
          {task.automationStartPossible && (
            <Button
              borderRadius={8}
              width="fit-content"
              colorScheme="purple"
              px={4}
              leftIcon={<SiProbot size={18} />}
              disabled={startTaskAutomationMutation.isPending || !canRetryAutomation}
              onClick={onOpenStartAutomation}
            >
              {t('components:start_automation_modal.action')}
            </Button>
          )}
          <Button borderRadius={8} width="fit-content" colorScheme="blue" px={4} leftIcon={<FiUserPlus size={18} />} onClick={onOpenAssignSomeone}>
            {!(assignee?.id !== currentUserId)
              ? t('components:assign_card.reassign.button.label')
              : t('components:assign_card.assign_to_another.button.label')}
          </Button>
          {assignee?.id !== currentUserId && (
            <Button borderRadius={8} width="fit-content" colorScheme="blue" px={4} leftIcon={<FiUser size={18} />} onClick={onOpenAssignMe}>
              {t('components:assign_card.assign.button.label')}
            </Button>
          )}
        </>
      )}

      <Modal isOpen={isPriorityModalOpen} onClose={handleDiscard}>
        <ModalOverlay />
        <ModalContent maxW="500px">
          <ModalHeader>
            {t('components:assign_card.task_priority.label')}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text mb={4}>{t('components:assign_card.task_priority.description')}</Text>
            <Stack spacing={5}>
              {PRIORITY_MAP.map((priority) => (
                <PriorityButton
                  key={priority.value}
                  icon={priority.icon}
                  text={priority.text}
                  value={priority.value}
                  isCurrentPriority={priority.value === taskPriority}
                  isSelected={selectedPriority === priority.value}
                  onClick={() => setSelectedPriority(priority.value)}
                  disabled={isRegistrationOfPortalInProgress}
                />
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="flex-end" gap={2}>
              <Button borderRadius={8} variant="solid" onClick={handleDiscard}>
                {t('common:discard')}
              </Button>
              <Button borderRadius={8} colorScheme="blue" onClick={handleConfirm}>
                {t('common:confirm')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenAssignSomeone} onClose={onCloseAssignSomeone}>
        <ModalOverlay />
        <ModalContent maxW="500px">
          <ModalHeader>
            {!(assignee?.id !== currentUserId)
              ? t('components:assign_card.reassign.button.label')
              : t('components:assign_card.assign_to_another.button.label')}
            <ModalCloseButton />
          </ModalHeader>
          <Container as="form" onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              {dropdown({
                name: 'user',
                label: t('forms:assignee.label'),
                control,
                errors,
                options: opUserOptions,
              })}
            </ModalBody>
            <ModalFooter>
              <Flex w="100%" justify="flex-end" gap={2}>
                <Button borderRadius={8} variant="solid" onClick={onCloseAssignSomeone}>
                  {t('common:discard')}
                </Button>

                <Button borderRadius={8} type="submit" variant="primary">
                  {!isLoading ? (
                    !(assignee?.id !== currentUserId) ? (
                      t('components:assign_card.reassign.button.label')
                    ) : (
                      t('components:assign_card.assign_to_another.button.label')
                    )
                  ) : (
                    <ButtonSpinner />
                  )}
                </Button>
              </Flex>
            </ModalFooter>
          </Container>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenAssignMe} onClose={onCloseAssignMe}>
        <ModalOverlay />
        <ModalContent maxW="500px">
          <ModalHeader>
            {assignee === null ? t('components:assign_card.assign.header.label') : t('components:assign_card.reassign.header.label')}
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody>
            {assignee === null
              ? t('components:assign_card.assign.description')
              : t('components:assign_card.reassign.description', {
                  assignee: assignee?.name,
                })}
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="flex-end" gap={2}>
              <Button borderRadius={8} variant="solid" onClick={onCloseAssignMe}>
                {t('common:discard')}
              </Button>

              <Button
                borderRadius={8}
                type="submit"
                variant="primary"
                onClick={() => {
                  assignee === null ? assign() : reassign();
                  onCloseAssignMe();
                }}
              >
                {assignee === null ? t('components:assign_card.assign.button.label') : t('components:assign_card.reassign.button.label')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenRetryAutomation} onClose={onCloseRetryAutomation}>
        <ModalOverlay />
        <ModalContent maxW="500px">
          <ModalHeader>
            {t('components:retry_automation_modal.header')}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Trans i18nKey="components:retry_automation_modal.message" />
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="flex-end" gap={2}>
              <Button borderRadius={8} variant="solid" onClick={onCloseRetryAutomation}>
                {t('common:cancel')}
              </Button>
              <Button
                borderRadius={8}
                variant="primary"
                disabled={retryTaskAutomationMutation.isPending}
                onClick={() =>
                  retryTaskAutomationMutation.mutate(task.id, {
                    onSuccess: () => {
                      onCloseRetryAutomation();
                      queryToInvalidate();
                    },
                    onError: (error) => {
                      toast({ title: error.message || t('errors:general'), status: 'error' });
                      onCloseRetryAutomation();
                      // the task might have changed in the meantime (e.g. automation already started), so reload it
                      queryToInvalidate();
                    },
                  })
                }
              >
                {retryTaskAutomationMutation.isPending ? <ButtonSpinner /> : t('components:retry_automation_modal.action')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenStartAutomation} onClose={onCloseStartAutomation}>
        <ModalOverlay />
        <ModalContent maxW="500px">
          <ModalHeader>
            {t('components:start_automation_modal.header')}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Trans i18nKey="components:start_automation_modal.message" />
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="flex-end" gap={2}>
              <Button borderRadius={8} variant="solid" onClick={onCloseStartAutomation}>
                {t('common:cancel')}
              </Button>
              <Button
                borderRadius={8}
                variant="primary"
                disabled={startTaskAutomationMutation.isPending}
                onClick={() =>
                  startTaskAutomationMutation.mutate(task.id, {
                    onSuccess: () => {
                      onCloseStartAutomation();
                      queryToInvalidate();
                    },
                    onError: (error) => {
                      toast({ title: error.message || t('errors:general'), status: 'error' });
                      onCloseStartAutomation();
                      // the task might have changed in the meantime (e.g. automation already started), so reload it
                      queryToInvalidate();
                    },
                  })
                }
              >
                {startTaskAutomationMutation.isPending ? <ButtonSpinner /> : t('components:start_automation_modal.action')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default TaskBannerCard;
